/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import ClauseCard from './ClauseCard';
import { highlightedSentence } from '../../hooks/Helper';
import { RootState, useTypedSelector } from '../../redux/store';

interface IProps {
  issues?: any;
  openBoot: VoidFunction;
  setBotFrom: any;
}

const issues = (props: IProps) => {
  const { issues, openBoot, setBotFrom } = props;

  const { instance } = useTypedSelector((state: RootState) => state.draft);

  const [expanded, setExpanded] = React.useState<string | false>(false);
  const [activePara, setActivePara] = React.useState<{
    id: string;
    index: number;
  } | null>();
  const [childOpen, setChildOpen] = React.useState(false);

  const getColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'unacceptable':
        return 'red';
      case 'needs review':
      case 'cannot determine':
      case 'cannot determine automatically':
        return 'orange';
      default:
        return 'green';
    }
  };

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const handleSentenceClick = (sentence: string, index: number, id: string) => {
    setActivePara({ id, index });
    highlightedSentence(sentence, instance);
  };

  const renderPara = (content: any, data: any) => {
    const contents = Object.keys(content);
    if (contents.length) {
      return contents.map((key, index) => {
        const clauseData = content[key];
        return (
          <Stack key={`${data?.section_name}-issue-para-${index}`}>
            {clauseData && (
              <Tooltip
                title={
                  Array.isArray(clauseData.reason)
                    ? clauseData.reason.map((item: string) => (
                        <li key={`${data?.section_name}-issue-reason-${item}`}>
                          {item}
                        </li>
                      ))
                    : clauseData.reason
                }
                open={childOpen}
                disableHoverListener
              >
                <IconButton
                  onMouseEnter={() => setChildOpen(true)}
                  onMouseLeave={() => setChildOpen(false)}
                  sx={{
                    padding: '0 10px',
                    borderRadius: 0,
                    '&:hover': { background: 'transparent' },
                    alignSelf: 'start',
                  }}
                >
                  <Chip
                    label={
                      Array.isArray(clauseData.rating)
                        ? clauseData.rating[0]
                        : clauseData.rating
                    }
                    sx={{
                      background: getColor(
                        Array.isArray(clauseData.rating)
                          ? clauseData.rating[0]
                          : clauseData.rating
                      ),
                      padding: '12px 8px',
                      borderRadius: '5px',
                      color: '#fff',
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
            {Array.isArray(clauseData?.summary) ? (
              clauseData?.summary?.map((para: string, index: number) => (
                <ClauseCard
                  onClick={() => handleSentenceClick(para, index, data?.id)}
                  key={`${data?.section_name}-issue-summary-${index}`}
                  index={index}
                  isActive={
                    activePara?.index === index && activePara?.id === data?.id
                  }
                >
                  <Typography>{para}</Typography>
                </ClauseCard>
              ))
            ) : clauseData?.summary ? (
              <ClauseCard
                onClick={() =>
                  handleSentenceClick(clauseData.summary, index, data.id)
                }
                key={`${data?.section_name}-issue-summary-${index}`}
                index={index}
                isActive={
                  activePara?.index === index && activePara?.id === data.id
                }
              >
                <Typography>{clauseData.summary}</Typography>
              </ClauseCard>
            ) : (
              <ClauseCard
                onClick={() => {}}
                key={`${data?.section_name}-issue-summary-not-found${index}`}
                index={index}
                isActive={false}
              >
                <Typography>Not Found</Typography>
              </ClauseCard>
            )}
          </Stack>
        );
      });
    }
  };

  return (
    <div>
      {!issues?.pending &&
        issues?.data?.map((data: any, index: any) => (
          <React.Fragment key={`${data?.section_name}-${index}`}>
            {Array.isArray(data?.q2_normalized_content) &&
              data?.q2_normalized_content?.length > 0 && (
                <Accordion
                  key={`${data?.section_name}-issue-accordion-${index}`}
                  onChange={handleChange(data?.id)}
                  expanded={expanded === data?.id}
                  className="according-class"
                >
                  <AccordionSummary>
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'center',
                      }}
                    >
                      <Typography>{data?.clause || ''}</Typography>
                      <Stack>
                        <Typography>
                          {data?.q2_normalized_content?.length}
                        </Typography>
                      </Stack>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack>
                      <Stack className="ulta-ai-button-container">
                        <Button
                          className="ulta-ai-button"
                          sx={{
                            alignSelf: 'end',
                            padding: '1.5px 10px',
                            backgroundColor: 'white',
                            margin: '2px 3px',
                            borderRadius: '5px',
                            width: 'fit-content',
                            '&:hover': {
                              backgroundColor: 'white',
                            },
                          }}
                          onClick={() => {
                            openBoot();
                            setBotFrom({
                              type: 'issues',
                              content: {
                                clause: data.clause,
                                data: data?.q1_normalized_content,
                                issues: data?.q2_normalized_content,
                              },
                            });
                          }}
                          startIcon={<ChatBubbleOutlineIcon />}
                          size="small"
                        >
                          Ask Ultra AI
                        </Button>
                      </Stack>
                    </Stack>
                    {data?.point_of_determination && (
                      <Typography>{data?.point_of_determination}</Typography>
                    )}
                    {data?.q2_normalized_content?.map((content: any) =>
                      renderPara(content, data)
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
          </React.Fragment>
        ))}
    </div>
  );
};

export default issues;
